/**
 * Utility for managing authentication state in localStorage.
 * Centralizes all auth-related storage operations to ensure consistency.
 */

const TOKEN_KEY = "website_jwt_token";
const USER_KEY = "website_user";
const USER_TYPE_KEY = "website_userType";
const COMMON_TOKEN_KEY = "website_commonToken";

/**
 * Saves authentication data to localStorage.
 * @param {string} token - The auth token.
 * @param {object} user - The user object.
 * @param {string} userType - The type of user (e.g., 'common').
 */
export const saveAuthData = (token, user, userType = "common") => {
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(COMMON_TOKEN_KEY, token);
    }
    if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
    if (userType) {
        localStorage.setItem(USER_TYPE_KEY, userType);
    }
};

/**
 * Retrieves stored user data.
 * @returns {object|null} The parsed user object.
 */
export const getUser = () => {
    const user = localStorage.getItem(USER_KEY);
    try {
        return user ? JSON.parse(user) : null;
    } catch (e) {
        console.error("Error parsing user data from localStorage", e);
        return null;
    }
};

/**
 * Retrieves stored auth token.
 * @returns {string|null} The token.
 */
export const getToken = () => {
    return localStorage.getItem(TOKEN_KEY);
};

/**
 * Synchronizes updated user data back to storage.
 * @param {object} userData - Updated user object or partial update.
 */
export const syncUser = (userData) => {
    const currentUser = getUser();
    if (currentUser) {
        const updatedUser = { ...currentUser, ...userData };
        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
    }
};

/**
 * Clears authentication data from localStorage (Logout).
 */
export const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(USER_TYPE_KEY);
    localStorage.removeItem(COMMON_TOKEN_KEY);
    // You might also want to clear sessionStorage just in case legacy code is still using it
    sessionStorage.clear();
};

/**
 * Checks if a user is currently logged in.
 * @returns {boolean} True if token and user exist.
 */
export const isLoggedIn = () => {
    return !!localStorage.getItem(TOKEN_KEY) && !!localStorage.getItem(USER_KEY);
};

/**
 * Checks if the currently logged-in user is a business customer.
 * @returns {boolean} True if user exists and customerType is 'business'.
 */
// Where to go after signing in. The browser history cannot be trusted for this: someone who came from the
// sign up page, or who switched between the sign in and sign up pages, would be sent "back" to sign up.
const AUTH_PATHS = ["/signin", "/signup", "/business/signin", "/business/signup"];
const isAuthPath = (path = "") => AUTH_PATHS.includes(path) || path.startsWith("/reset-password");

// called on every page change; remembers the last page that is not a sign in / sign up page
export const rememberPage = (pathname, search = "") => {
    try {
        if (!isAuthPath(pathname)) sessionStorage.setItem("lastShopPath", pathname + search);
    } catch (e) { /* storage can be blocked */ }
};

// the page a protected route sent the visitor from, else the last page they were shopping on, else home
export const getReturnPath = (state) => {
    const from = state?.from;
    if (from?.pathname && !isAuthPath(from.pathname)) return from.pathname + (from.search || "");
    try {
        const last = sessionStorage.getItem("lastShopPath");
        if (last && !isAuthPath(last.split("?")[0])) return last;
    } catch (e) { /* storage can be blocked */ }
    return "/";
};

export const getIsBusiness = () => {
    const user = getUser();
    return user?.customerType === 'business';
};

export default {
    saveAuthData,
    getUser,
    getToken,
    syncUser,
    logout,
    isLoggedIn,
    getIsBusiness
};
